import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';

const approveStatusMap = {
    0: { label: 'Beklemede', severity: 'warning' },
    1: { label: 'Onaylandı', severity: 'success' },
    2: { label: 'Reddedildi', severity: 'danger' },
    3: { label: 'İptal Edildi', severity: 'secondary' }
};

const cancelStatusMap = {
    1: { label: 'İptal Bekliyor', severity: 'warning', key: 'pending' },
    2: { label: 'İptal Edildi', severity: 'danger', key: 'approved' },
    3: { label: 'İptal Reddedildi', severity: 'info', key: 'rejected' }
};

const RetailSalesTable = ({
    th,
    data,
    totalRecords,
    lazyParams,
    onPage,
    onSort,
    loading,
    onViewDetail,
    onDownloadPdf,
    downloadingPdfId,
    onRequestCancel,
    showCancelInfo = false,
    variant = 'default' // 'default' | 'cancelApproval'
}) => {
    const dateBody = (val) => (val ? new Date(val).toLocaleDateString('tr-TR') : '');
    const dateTimeBody = (val) => (val ? new Date(val).toLocaleString('tr-TR') : '');

    const amountBody = (row) =>
        row?.netTotalAmount != null
            ? Number(row.netTotalAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })
            : '';

    const statusBody = (row) => {
        const cancelStatus = cancelStatusMap[row.cancelApproveStatus];
        if (cancelStatus) {
            const label = th(`retailSales.cancelStatus.${cancelStatus.key}`) || cancelStatus.label;
            return <Tag value={label} severity={cancelStatus.severity} />;
        }
        const status = approveStatusMap[row.approveStatus] || approveStatusMap[0];
        return <Tag value={th(`retailSales.status.${row.approveStatus}`) || status.label} severity={status.severity} />;
    };

    const canRequestCancel = (row) => {
        if (!onRequestCancel) return false;
        if (row?.approveStatus === 3) return false; // already canceled
        if (row?.approveStatus === 2) return false; // rejected sale cannot be canceled
        if (row?.cancelApproveStatus === 1) return false; // already pending
        if (row?.cancelApproveStatus === 2) return false; // already approved cancel
        return true;
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex justify-content-center gap-1">
            <Button
                icon="pi pi-eye"
                className="p-button-rounded p-button-text p-button-sm"
                onClick={() => onViewDetail?.(rowData)}
                tooltip={th('common.view')}
                tooltipOptions={{ position: 'top' }}
            />
            {onDownloadPdf && (
                <Button
                    icon="pi pi-file-pdf"
                    className="p-button-rounded p-button-text p-button-sm p-button-danger"
                    onClick={() => onDownloadPdf(rowData)}
                    loading={downloadingPdfId === rowData.id}
                    disabled={downloadingPdfId != null && downloadingPdfId !== rowData.id}
                    tooltip={th('retailSales.pdf.downloadPdf')}
                    tooltipOptions={{ position: 'top' }}
                />
            )}
            {variant === 'default' && canRequestCancel(rowData) && (
                <Button
                    icon="pi pi-ban"
                    className="p-button-rounded p-button-text p-button-sm p-button-danger"
                    onClick={() => onRequestCancel(rowData)}
                    tooltip={th('retailSales.cancel')}
                    tooltipOptions={{ position: 'top' }}
                />
            )}
        </div>
    );

    return (
        <DataTable
            value={data}
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows}
            totalRecords={totalRecords}
            onPage={onPage}
            onSort={onSort}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            className="p-datatable-gridlines"
            showGridlines
            dataKey="id"
            loading={loading}
            rowsPerPageOptions={[5, 10, 25, 50]}
            tableStyle={{ minWidth: '60rem' }}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate={th('table.paginatorTemplate')}
            emptyMessage={th('common.noRecords')}
        >
            <Column
                field="invoiceSeriNo"
                header={th('retailSales.invoiceSeriNo')}
                sortable
                style={{ width: '13%' }}
            />
            <Column
                field="invoiceDate"
                header={th('retailSales.invoiceDate')}
                sortable
                body={(row) => dateBody(row.invoiceDate)}
                style={{ width: '10%' }}
            />
            <Column
                field="maturityDate"
                header={th('retailSales.maturityDate')}
                sortable
                body={(row) => dateBody(row.maturityDate)}
                style={{ width: '10%' }}
            />
            <Column
                field="customer"
                header={th('retailSales.customer')}
                sortable
                style={{ width: showCancelInfo ? '15%' : '20%' }}
            />
            <Column
                field="netTotalAmount"
                header={th('retailSales.netTotalAmount')}
                sortable
                body={amountBody}
                style={{ width: '12%', textAlign: 'right' }}
            />
            {showCancelInfo && (
                <Column
                    field="cancelRequestedName"
                    header={th('retailSales.cancelRequestedBy')}
                    body={(row) => row.cancelRequestedName || '-'}
                    style={{ width: '12%' }}
                />
            )}
            {showCancelInfo && (
                <Column
                    field="cancelRequestedDate"
                    header={th('retailSales.cancelRequestedDate')}
                    body={(row) => dateTimeBody(row.cancelRequestedDate)}
                    style={{ width: '12%' }}
                />
            )}
            <Column
                field="approveStatus"
                header={th('retailSales.approveStatus')}
                body={statusBody}
                style={{ width: '10%', textAlign: 'center' }}
            />
            <Column
                body={actionBodyTemplate}
                exportable={false}
                style={{ width: '10%', minWidth: '120px' }}
            />
        </DataTable>
    );
};

export default RetailSalesTable;
